import { useDroppable } from '@dnd-kit/core';
import { useCallback, useMemo } from 'react';
import { FaAngleDown, FaAngleRight, FaRegFolder } from 'react-icons/fa6';

import { useFileTreeContext } from '@/pages/insight/folders/use-file-tree-context';

import { FolderHeader } from './folder-header';
import { FolderToggle } from './folder-toggle';
import { useCategoryFolder } from '../util/category-folder';
import { TreeEntry, TreeFolder, isTreeFolder } from '../util/document-list-to-tree';
import { useFolderActions } from '../util/folder-actions';
import { useSummaryCount } from '../util/summary-count';

interface CategoryProps {
  title: string;
  entry: TreeEntry;
  /** Render mode for different use cases */
  mode?: 'default' | 'accordion-header' | 'clickable';
  /** Custom click handler override */
  onCustomClick?: () => void;
  /** Whether to show the expand/collapse toggle */
  showToggle?: boolean;
  /** Custom styling classes */
  className?: string;
  /** Whether to show document count */
  showDocumentCount?: boolean;
}

/**
 * Reusable Category component that can render in different modes
 *
 * This component consolidates common folder functionality and can be used
 * as a regular category, accordion header, or clickable category.
 *
 * @param props - Component props with rendering configuration
 * @returns Rendered folder category with specified functionality
 */
export const Category = ({
  title,
  entry,
  mode = 'default',
  onCustomClick,
  showToggle = true,
  className = '',
  showDocumentCount = true,
}: CategoryProps) => {
  const folderState = useCategoryFolder(entry);
  const { setActiveFolderId, setPage, summaryCount } = useFileTreeContext();

  const { setNodeRef, isOver } = useDroppable({
    id: entry?.id || 'fallback-id',
  });

  const { activeFolderId } = useFileTreeContext();

  const folderForActions = folderState?.entry || (isTreeFolder(entry) ? entry : null);
  const handleFolderClick = useFolderActions(folderForActions!);

  // Handle different click behaviors based on mode
  const handleClick = useCallback(() => {
    if (onCustomClick) {
      onCustomClick();
    } else if (mode === 'clickable') {
      setActiveFolderId(entry.id);
      setPage(1);
    } else {
      handleFolderClick();
    }
  }, [onCustomClick, mode, entry.id, setActiveFolderId, setPage, handleFolderClick]);

  const handleToggleClick = useCallback(() => {
    folderState?.toggleOpen();
  }, [folderState]);

  // Memoized function for rendering child categories with proper spacing
  const renderSubCategories = useCallback((subCategories: TreeFolder[]) => {
    return subCategories.map((child: TreeFolder) => (
      <div className="py-0.5 ml-2" key={child.id}>
        <Category title={child.toDisplayString()} entry={child} />
      </div>
    ));
  }, []);

  const isActive = useMemo(
    () => activeFolderId === (folderState?.entry?.id || entry?.id),
    [activeFolderId, folderState?.entry?.id, entry?.id],
  );

  // Base classes with mode-specific styling
  const baseClasses = useMemo(() => {
    let classes = 'flex items-center justify-between cursor-pointer rounded-lg';

    if (mode === 'accordion-header') {
      classes =
        'flex items-center justify-between w-full py-2 px-3 cursor-pointer rounded-lg hover:bg-day-light-3';
    } else if (mode === 'clickable') {
      classes =
        'flex justify-between items-center w-full transition duration-300 cursor-pointer rounded-md text-sm relative hover:bg-day-light-3 bg-day-light-5';
    } else {
      classes += ' hover:rounded-lg hover:bg-day-light-3';
    }

    if (isOver && mode !== 'accordion-header') classes += ' bg-day-light-1';
    if (isActive && mode !== 'accordion-header') classes += ' bg-day-light-3';

    return classes;
  }, [mode, isOver, isActive]);

  const finalClasses = `${baseClasses} ${className}`.trim();

  // Memoized subcategories with type filtering for performance
  const subCategories = useMemo(() => {
    const folderEntry = folderState?.entry;
    if (!folderEntry?.children) return [];
    return [...folderEntry.children.values()].filter(isTreeFolder);
  }, [folderState?.entry]) as TreeFolder[];

  const folderForCount = folderState?.entry || (isTreeFolder(entry) ? entry : null);
  const documentCount = useSummaryCount(folderForCount!, summaryCount || {});

  if (!entry || !entry.id) {
    console.error('Category component received invalid entry:', entry);
    return <div className="text-red-500 text-sm p-2">Invalid folder data</div>;
  }

  if (!folderState && mode !== 'accordion-header') {
    return <div className="text-gray-500 text-sm p-2">Loading folder...</div>;
  }

  // For accordion header mode, render simplified version
  if (mode === 'accordion-header') {
    return (
      <div className={finalClasses}>
        <div className="flex items-center">
          <div className="m-3 w-[14px] h-[14px] flex items-center justify-center">
            <span className="text-day-neutral-subtle">
              <FaRegFolder />
            </span>
          </div>
          <span className="text-sm text-day-neutral-subtle">
            {title} {showDocumentCount ? `(${documentCount})` : ''}
          </span>
        </div>
      </div>
    );
  }

  // For clickable mode, render with shadow and custom styling
  if (mode === 'clickable') {
    return (
      <div className="shadow mb-4 rounded-md">
        <div
          className={finalClasses}
          onClick={handleClick}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleClick();
            }
          }}
        >
          <div className="w-full text-left z-0 py-2 px-3">
            <div className="flex items-center">
              <div className="m-3 w-[14px] h-[14px] flex items-center justify-center">
                <span className="text-day-neutral-subtle">
                  <FaRegFolder />
                </span>
              </div>
              <span className="text-sm text-day-neutral-subtle">
                {title} {showDocumentCount ? `(${documentCount})` : ''}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default mode - full functionality
  const { isOpen, contentHeight, accordionRef, contentRef } = folderState!;

  return (
    <>
      <div className={finalClasses} ref={setNodeRef}>
        <FolderHeader
          title={title}
          documentCount={showDocumentCount ? documentCount : 0}
          onClick={handleClick}
        />

        {subCategories.length && showToggle ? (
          <span
            className="p-2 h-full flex items-center"
            role="button"
            tabIndex={0}
            aria-label={isOpen ? 'Collapse folder' : 'Expand folder'}
            onClick={(event) => {
              event.stopPropagation();
              handleToggleClick();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleToggleClick();
              }
            }}
          >
            {isOpen ? (
              <FaAngleDown className="text-day-neutral-subtle w-3 h-3" />
            ) : (
              <FaAngleRight className="text-day-neutral-subtle w-3 h-3" />
            )}
          </span>
        ) : null}
      </div>

      <FolderToggle
        contentHeight={contentHeight}
        accordionRef={accordionRef}
        isOpen={isOpen}
        renderSubCategories={renderSubCategories}
        subCategories={subCategories}
        contentRef={contentRef}
      />
    </>
  );
};
